import React, { useEffect, useState } from "react";
import {
  Link,
  LinkSizeEnum,
} from "../../../../../../components/core/Link/Link";
import {
  AlignmentEnum,
  FontWeightEnum,
} from "../../../../../../types/AppTypes";
import {
  CATEGORY_LABEL,
  COURSE_CERTIFICATE_LABEL,
  COURSE_LABEL,
  COURSES_LABEL,
  DELETE_COURSE_BODY_LABEL_1,
  DELETE_COURSE_LABEL,
  DESCRIPTION_LABEL,
  DURATION_LABEL,
  EMPTY_COURSES_MESSAGE,
  EMPTY_STRING,
  EXECUTION_MODE_LABEL,
  NAME_LABEL,
  TYPOLOGY_LABEL,
  URL_LABEL,
} from "../../../../../../constants/Dictionary";
import { ReactComponent as AddIcon } from "../../../../../../icons/add.svg";
import { BodyTyp } from "../../../../../../components/Typography/Typography";
import SearchInput from "../../../../../../components/core/SearchInput/SearchInput";
import { DoubleStructure } from "../../../../../../components/core/DoubleStructure/DoubleStructure";
import { Loader } from "../../../../../../components/core/Loader/Loader";
import { StyledCourses } from "../../../../../Formation/Courses/Courses.styled";
import { StyledExpandTable } from "../../ProfessionalHistory.styled";
import { PopupConfirm } from "../../../../../../components/PopupConfirm/PopupConfirm";
import emptyCoursesImage from "../../../../../../images/courses.png";
import { EmptyState } from "../../../../../../components/EmptyState/EmptyState";
import {
  Button,
  ButtonVariants,
} from "../../../../../../components/core/Button/Button";
import { COLLABORATORS_PAGE_SIZE } from "../../../../../../constants/AppConstants";
import {
  CollaboratorCmSheet,
  CollaboratorDetails,
  CollaboratorHistoryCourse,
} from "../../../../../../types/api/collaborators.type";
import { useDebounce } from "use-debounce";
import { HistoryCourseModal } from "./HistoryCourseModal/HistoryCourseModal";
import { getCollaboratorHistoryCoursesColumns } from "../../../../../../constants/TableColumns";
import { CertificateModal } from "../../../../../../components/CertificateModal/CertificateModal";
import { useAppSelector } from "../../../../../../hooks/store.hook";
import { findLabelFromValue } from "../../../../../../utils/orgFunctions.utils";
import {
  TextBoxGrid,
  TextBox,
} from "../../../../../../components/TextBoxGrid/TextBoxGrid.component";
import CollaboratorsHistoryCertificateOfCoursesService from "../../../../../../services/api/collaborators/CollaboratorsHistoryCertificateOfCourses.service";
import {
  Table,
  TableThemeEnum,
} from "../../../../../../components/core/Table/Table";
import LoadingThumbnail from "../../../../../../components/LoadingThumbnail/LoadingThumbnail";
import { canEditCollaboratorHistory } from "../../../../../../utils/canEditCollaboratorHistory.utils";
import {
  useCourseHistoryDelete,
  useCourseHistory,
} from "../../../../../../hooks/formations.hook";

type CoursesHistoryProps = {
  setLink: (component: React.ReactNode) => void;
  collaborator: CollaboratorDetails;
  onStatusRefresh: () => void;
  isMyProfile?: boolean;
  currentCmSheet?: CollaboratorCmSheet;
};

export const CoursesHistory = ({
  setLink,
  collaborator,
  onStatusRefresh,
  isMyProfile,
  currentCmSheet,
}: CoursesHistoryProps) => {
  const [selectedCourse, setSelectedCourse] =
    useState<CollaboratorHistoryCourse | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [searchDebouncedValue] = useDebounce(searchValue, 500);
  const [pageNumber, setPageNumber] = useState(0);

  const { profile } = useAppSelector((state) => state.user);
  const { orgSettings } = useAppSelector((state) => state.config);
  const profileId = collaborator.id;

  const certificateApi = (certificationId: number) =>
    CollaboratorsHistoryCertificateOfCoursesService.get(
      profileId,
      certificationId,
    );

  const {
    data: historyCourses,
    isLoading: coursesLoading,
    isFetching: coursesFetching,
  } = useCourseHistory({
    profileId,
    pageNumber,
    searchDebouncedValue,
  });

  const hasData =
    historyCourses !== undefined ? historyCourses.pagination.total > 0 : null;
  const { mutateAsync: deleteCourse, isPending: pendingDeleteCourse } =
    useCourseHistoryDelete(profileId);

  const canEditHistory = canEditCollaboratorHistory(
    profile,
    collaborator,
    currentCmSheet,
    isMyProfile,
  );

  const handleDeleteCourse = async () => {
    await deleteCourse(selectedCourse!.id);
    hideDeletePopup();
    onStatusRefresh();
    setSelectedCourse(null);
    setShowDeleteModal(false);
  };

  useEffect(() => {
    if (hasData && canEditHistory) {
      setLink(
        <Link
          onClick={() => setShowAddModal(true)}
          icon={<AddIcon />}
          size={LinkSizeEnum.SMALL}
        >
          {COURSE_LABEL}
        </Link>,
      );
    }
    return () => setLink(null);
  }, [hasData, canEditHistory]);

  const onEdit = (record: CollaboratorHistoryCourse) => {
    setSelectedCourse(record);
    setShowAddModal(true);
  };

  const hideAddModal = () => {
    setSelectedCourse(null);
    setShowAddModal(false);
  };

  const handleSave = () => {
    hideAddModal();
    onStatusRefresh();
  };

  const onDelete = (record: CollaboratorHistoryCourse) => {
    setSelectedCourse(record);
    setShowDeleteModal(true);
  };

  const hideDeletePopup = () => {
    setSelectedCourse(null);
    setShowDeleteModal(false);
  };

  const onView = (record: CollaboratorHistoryCourse) => {
    setSelectedCourse(record);
    setShowViewModal(true);
  };

  const rowKey = (record: CollaboratorHistoryCourse) => {
    return record.id.toString();
  };

  const expandedRow = (
    record: CollaboratorHistoryCourse,
  ): React.ReactElement | null => {
    return (
      <>
        <TextBoxGrid
          numCol={3}
          items={[
            {
              label: DURATION_LABEL,
              text: record.course.duration ?? EMPTY_STRING,
            },
            {
              label: TYPOLOGY_LABEL,
              text: findLabelFromValue(
                orgSettings.courses.typologies,
                record.course.typology,
              ),
            },
            {
              label: CATEGORY_LABEL,
              text: findLabelFromValue(
                orgSettings.courses.categories,
                record.course.category,
              ),
            },
          ]}
        />
        <TextBoxGrid
          numCol={3}
          items={[
            {
              label: EXECUTION_MODE_LABEL,
              text: findLabelFromValue(
                orgSettings.courses.executionModes,
                record.course.executionMode,
              ),
            },
            {
              label: URL_LABEL,
              text: record.course.url,
            },
          ]}
        />
        <TextBox label={DESCRIPTION_LABEL} text={record.course.description} />
      </>
    );
  };

  const hideDeleteIconFromCmSheet = (row: CollaboratorHistoryCourse) => {
    return row.fromCmSheet === false;
  };

  return (
    <>
      <StyledCourses>
        {coursesLoading && !searchDebouncedValue && hasData === null ? (
          <Loader loading={true}>
            <LoadingThumbnail marginTop={316} />
          </Loader>
        ) : (
          <>
            {!hasData && !searchDebouncedValue ? (
              <div className="empty-state">
                <EmptyState
                  label={EMPTY_COURSES_MESSAGE}
                  imageSrc={emptyCoursesImage}
                >
                  {canEditHistory && (
                    <Button
                      label={COURSE_LABEL}
                      variant={ButtonVariants.SECONDARY}
                      icon={<AddIcon />}
                      onClick={() => {
                        setShowAddModal(true);
                      }}
                    />
                  )}
                </EmptyState>
              </div>
            ) : (
              <>
                <DoubleStructure verticalAlignment={AlignmentEnum.CENTER}>
                  <BodyTyp variant="2" fontWeight={FontWeightEnum.MEDIUM}>
                    {!coursesFetching &&
                      `${historyCourses?.pagination.total} ${historyCourses?.pagination.total == 1 ? COURSE_LABEL : COURSES_LABEL}`}
                  </BodyTyp>
                  <div className="filters">
                    <SearchInput
                      onChange={setSearchValue}
                      label={NAME_LABEL}
                      name={"search"}
                      value={searchValue}
                    />
                  </div>
                </DoubleStructure>
                <StyledExpandTable>
                  <Table<CollaboratorHistoryCourse>
                    theme={TableThemeEnum.DARK}
                    loading={coursesFetching}
                    rowKey={rowKey}
                    headers={getCollaboratorHistoryCoursesColumns(onView)}
                    scrollY={"calc(100dvh - 502px)"}
                    onEdit={canEditHistory ? onEdit : undefined}
                    onDelete={canEditHistory ? onDelete : undefined}
                    rowHasDelete={hideDeleteIconFromCmSheet}
                    data={historyCourses?.data || []}
                    pagination={{
                      current: pageNumber + 1,
                      onChange: (pageNumber) => setPageNumber(pageNumber - 1),
                      total: historyCourses?.pagination.total ?? 0,
                      pageSize: COLLABORATORS_PAGE_SIZE,
                    }}
                    expandable={{
                      expandedRowRender: expandedRow,
                      rowExpandable: (record) => !record.external,
                    }}
                    emptyText={EMPTY_COURSES_MESSAGE}
                  />
                </StyledExpandTable>
              </>
            )}
          </>
        )}
      </StyledCourses>
      <PopupConfirm
        isOpen={showDeleteModal}
        label={DELETE_COURSE_LABEL}
        popupText={`${DELETE_COURSE_BODY_LABEL_1} ${selectedCourse?.course.name}?`}
        onConfirm={() => {
          handleDeleteCourse();
        }}
        onCancel={() => {
          hideDeletePopup();
        }}
        isLoading={pendingDeleteCourse}
      />
      {showAddModal && (
        <HistoryCourseModal
          course={selectedCourse}
          isOpen={showAddModal}
          onSave={handleSave}
          onClose={() => hideAddModal()}
          collaborator={collaborator}
          certificateId={
            selectedCourse?.fileName ? selectedCourse.id : undefined
          }
        />
      )}
      <CertificateModal
        label={COURSE_CERTIFICATE_LABEL}
        isOpen={showViewModal}
        closeModal={() => {
          setShowViewModal(false);
          setSelectedCourse(null);
        }}
        {...(selectedCourse && {
          certificateApi: () => certificateApi(selectedCourse.id),
        })}
        idKeyApi={`history-course-${selectedCourse?.id}`}
      />
    </>
  );
};
